import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import {
  describeSelector,
  istDay,
  labelsMatch,
  listPriceComponents,
  listSellerGst,
  repricePriceRule,
  revertPriceChange,
  rulePays,
  sellerGstOf,
  speaksGst,
  type PriceComponent,
  type SellerGst,
} from "../../api/priceList";
import {
  KIND_WORDS,
  MARKDOWN,
  MAX_DOC_BYTES,
  base64ToBytes,
  base64ToText,
  bytesToBase64,
  applySteps,
  canKeepPriceListDocs,
  comparePriceListDoc,
  getPriceListDoc,
  stillAsCompared,
  type CompareLine,
  isEditable,
  lineage,
  listPriceListDocs,
  savePriceListDoc,
  saveBytes,
  textToBase64,
  type DocKind,
  type PriceListDoc,
} from "../../api/priceListDocs";

const KIND_COLOR: Record<DocKind, "default" | "primary" | "success" | "info" | "warning"> = {
  original: "primary",
  proposal: "info",
  saved: "default",
  applied: "success",
  snapshot: "warning",
};

const istTime = (iso: string) =>
  new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
const size = (n: number) => (n < 1024 ? `${n} B` : n < 1 << 20 ? `${(n / 1024).toFixed(1)} KB` : `${(n / (1 << 20)).toFixed(1)} MB`);

/**
 * Plans › Price-list documents (owner 2026-10-29): every rate card uploaded and
 * every price list read, saved, applied or downloaded -- the original is never
 * lost, and any version can be opened, edited into a new version, or
 * downloaded. Owner and Admin only (owner D2). Nothing here changes a price:
 * applying a version to billing is its own step, with a comparison first.
 */
export function PriceListDocumentsScreen() {
  const identity = useIdentity();
  const own = identity.namespace ?? "";
  const [target, setTarget] = useState(own);
  const [ns, setNs] = useState(own);
  const [open, setOpen] = useState<PriceListDoc | null>(null);
  const [reverting, setReverting] = useState<PriceListDoc | null>(null);
  const may = canKeepPriceListDocs(identity.roles);
  const docs = useQuery({
    queryKey: ["pricelistdocs", ns],
    enabled: Boolean(identity.token && ns && may),
    retry: false,
    queryFn: ({ signal }) => listPriceListDocs(identity, ns, signal),
  });

  if (!may) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Price-list documents are kept for the provider&apos;s Owner and Admin only.</Alert>
      </Box>
    );
  }
  const all = docs.data ?? [];
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Price-list documents
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every rate card you upload and every price list read from it, saved, applied or downloaded -- never lost.
        Open a price list to change it: your changes are kept as a new version, the old one stays.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField size="small" label="Namespace" value={target} sx={{ minWidth: 320 }}
          onChange={(e) => setTarget(e.target.value.trim().toLowerCase())} />
        <Button variant="outlined" sx={{ height: 40 }} onClick={() => setNs(target)}>
          Open
        </Button>
        {ns && <KeepFile ns={ns} />}
      </Stack>
      {docs.isLoading && <Typography variant="body2">Loading…</Typography>}
      {docs.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Documents not loaded</AlertTitle>
          {(docs.error as Error).message}
        </Alert>
      )}
      {docs.data && all.length === 0 && (
        <Alert severity="info">
          No documents kept for {ns} yet. Upload a rate card under Plans › Propose from a document, download the current
          price list, or keep a file here.
        </Alert>
      )}
      {all.length > 0 && (
        <Table size="small" aria-label="Price-list documents">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>What</TableCell>
              <TableCell>Title and file</TableCell>
              <TableCell>Made from</TableCell>
              <TableCell>Kept</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {all.map((d) => (
              <TableRow key={d.id}>
                <TableCell>{d.id}</TableCell>
                <TableCell>
                  <Chip size="small" label={KIND_WORDS[d.kind]} color={KIND_COLOR[d.kind]} />
                  {d.effectiveFrom && (
                    <Typography variant="caption" display="block">
                      from {d.effectiveFrom}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{d.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.filename} · {size(d.sizeBytes)}
                    {d.note ? ` · ${d.note}` : ""}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{lineage(d, all) || "—"}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {istTime(d.createdAt)}
                    <br />
                    {d.createdBy}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <DownloadDoc ns={ns} doc={d} />
                  {isEditable(d) && (
                    <Button size="small" onClick={() => setOpen(d)}>
                      Open
                    </Button>
                  )}
                  {isEditable(d) && (
                    <Button size="small" color="warning" onClick={() => setReverting(d)}>
                      Revert to this list
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {open && <EditDoc key={open.id} ns={ns} doc={open} onClose={() => setOpen(null)} />}
      {reverting && <RevertToList key={reverting.id} ns={ns} doc={reverting} onClose={() => setReverting(null)} />}
    </Box>
  );
}

function DownloadDoc({ ns, doc }: { ns: string; doc: PriceListDoc }) {
  const identity = useIdentity();
  const run = useMutation<void, Error>({
    mutationFn: async () => {
      const d = await getPriceListDoc(identity, ns, doc.id);
      saveBytes(base64ToBytes(d.contentBase64), d.filename, d.mediaType);
    },
  });
  return (
    <Button size="small" disabled={run.isPending} title={run.error?.message} color={run.isError ? "error" : "primary"}
      onClick={() => run.mutate()}>
      {run.isPending ? "…" : "Download"}
    </Button>
  );
}

/** Keep any file here: a rate card received by email, a price list made elsewhere. */
function KeepFile({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const run = useMutation<PriceListDoc, Error, File>({
    mutationFn: async (f) => {
      if (f.size > MAX_DOC_BYTES) throw new Error(`${f.name} is over 10 MB`);
      const md = /\.md$/i.test(f.name);
      return savePriceListDoc(identity, ns, {
        kind: md ? "saved" : "original",
        title: f.name.replace(/\.[^.]+$/, ""),
        filename: f.name,
        mediaType: md ? MARKDOWN : f.type || "application/octet-stream",
        contentBase64: bytesToBase64(new Uint8Array(await f.arrayBuffer())),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricelistdocs", ns] }),
  });
  return (
    <>
      <input ref={input} type="file" hidden accept=".pdf,.docx,.xlsx,.csv,.txt,.md"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) run.mutate(f);
          e.target.value = "";
        }} />
      <Button variant="outlined" sx={{ height: 40 }} disabled={run.isPending} onClick={() => input.current?.click()}>
        {run.isPending ? "Keeping…" : "Keep a file"}
      </Button>
      {run.isError && <Alert severity="error" sx={{ py: 0 }}>{run.error.message}</Alert>}
    </>
  );
}

/**
 * A price list opened as text. Save keeps the edit as a NEW version whose
 * parent is this one; Save & download also gives the file. Nothing is
 * overwritten and no price changes here.
 */
function EditDoc({ ns, doc, onClose }: { ns: string; doc: PriceListDoc; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const body = useQuery({
    queryKey: ["pricelistdoc", ns, doc.id],
    retry: false,
    queryFn: async () => base64ToText((await getPriceListDoc(identity, ns, doc.id)).contentBase64),
  });
  const [text, setText] = useState<string | null>(null);
  const [title, setTitle] = useState(doc.title);
  const [note, setNote] = useState("");
  const current = text ?? body.data ?? "";
  const changed = text !== null && text !== body.data;
  const save = useMutation<PriceListDoc, Error, boolean>({
    mutationFn: async (download) => {
      const stamp = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })
        .format(new Date()).replace(/[^0-9]/g, "");
      const filename = `${doc.filename.replace(/(-saved-\d+)?\.(md|txt)$/i, "")}-saved-${stamp}.md`;
      const saved = await savePriceListDoc(identity, ns, {
        kind: "saved",
        title: title.trim() || doc.title,
        filename,
        mediaType: MARKDOWN,
        contentBase64: textToBase64(current),
        parentId: doc.id,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      if (download) saveBytes(current, filename, MARKDOWN);
      return saved;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricelistdocs", ns] }),
  });
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {KIND_WORDS[doc.kind]} #{doc.id}: {doc.title}
      </DialogTitle>
      <DialogContent>
        {body.isLoading && <Typography variant="body2">Loading…</Typography>}
        {body.isError && <Alert severity="error">{(body.error as Error).message}</Alert>}
        {body.data !== undefined && (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField multiline minRows={18} maxRows={30} value={current} onChange={(e) => setText(e.target.value)}
              inputProps={{ spellCheck: false, style: { fontFamily: "monospace", fontSize: 12 } }} />
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="Title of the new version" value={title} sx={{ flex: 1 }}
                onChange={(e) => setTitle(e.target.value)} />
              <TextField size="small" label="Note (optional)" value={note} sx={{ flex: 2 }}
                onChange={(e) => setNote(e.target.value)} />
            </Stack>
            {save.isError && (
              <Alert severity="error">
                <AlertTitle>Not saved</AlertTitle>
                {save.error.message}
              </Alert>
            )}
            {save.data && <Alert severity="success">Saved as #{save.data.id}. #{doc.id} is unchanged.</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button disabled={!changed || save.isPending} onClick={() => save.mutate(false)}>
          Save
        </Button>
        <Button variant="contained" disabled={!changed || save.isPending} onClick={() => save.mutate(true)}>
          Save &amp; download
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const ACTION_WORDS: Record<CompareLine["action"], string> = {
  same: "already the same",
  revert: "undo the change",
  change: "change the price",
  none: "not changed here",
};

/** The GST of the component a line prices, from billing's quote; undefined when not known. */
function lineGst(l: CompareLine, comps: PriceComponent[], seller: SellerGst | undefined): number | undefined {
  const c = comps.find((x) => labelsMatch(l.labelselector, x.labels));
  return c ? sellerGstOf(c, seller) : undefined;
}

type StepResult = { line: CompareLine; ok: boolean; words: string };

/**
 * Revert to a previous list (owner 2026-10-29): compare the kept list with
 * billing, line by line, then bring billing back to it from a date with
 * Change price and Revert -- the same calls the Price list makes, each
 * idempotent in radbilling. Billing is compared again just before applying,
 * and a line that moved since is not touched. The list is kept as "Applied to
 * billing" only when every line succeeded; otherwise the failed lines are
 * shown and the run can be repeated.
 */
function RevertToList({ ns, doc, onClose }: { ns: string; doc: PriceListDoc; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const now = new Date();
  const [from, setFrom] = useState(istDay(now, 1));
  const compare = useQuery({
    queryKey: ["pricelistdoc-compare", ns, doc.id],
    retry: false,
    queryFn: ({ signal }) => comparePriceListDoc(identity, ns, doc.id, signal),
  });
  const comps = useQuery({
    queryKey: ["pricelist-components", ns],
    retry: false,
    queryFn: ({ signal }) => listPriceComponents(identity, ns, signal),
  });
  const seller = useQuery({
    queryKey: ["pricelist-gst", ns],
    retry: false,
    queryFn: ({ signal }) => listSellerGst(identity, ns, signal),
  });
  const lines = compare.data?.lines ?? [];
  const plan = applySteps(lines, from, now);
  const apply = useMutation<{ results: StepResult[]; kept?: PriceListDoc }, Error>({
    mutationFn: async () => {
      const fresh = (await comparePriceListDoc(identity, ns, doc.id)).lines;
      const results: StepResult[] = [];
      for (const st of plan.steps) {
        if (!stillAsCompared(st.line, fresh)) {
          results.push({ line: st.line, ok: false, words: "billing changed since you compared -- not applied; compare again" });
          continue;
        }
        try {
          if (st.kind === "change") {
            const r = await repricePriceRule(identity, ns, st.input);
            results.push({ line: st.line, ok: true, words: r.unchanged || r.replay ? "already done" : `changed from ${st.input.effectivefrom}` });
          } else {
            const r = await revertPriceChange(identity, ns, st.input);
            results.push({ line: st.line, ok: true, words: r.replay ? "already undone" : `undone from ${st.input.effectivefrom}` });
          }
        } catch (e) {
          results.push({ line: st.line, ok: false, words: (e as Error).message });
        }
      }
      void qc.invalidateQueries({ queryKey: ["pricelist", ns] });
      void qc.invalidateQueries({ queryKey: ["plan-detail-rules", ns] });
      if (results.length === 0 || results.some((r) => !r.ok)) return { results };
      const full = await getPriceListDoc(identity, ns, doc.id);
      const kept = await savePriceListDoc(identity, ns, {
        kind: "applied",
        title: `Applied: ${doc.title}`.slice(0, 255),
        filename: doc.filename,
        mediaType: doc.mediaType,
        contentBase64: full.contentBase64,
        parentId: doc.id,
        effectiveFrom: from,
        note: `${results.length} price(s) brought back to #${doc.id}`,
      });
      void qc.invalidateQueries({ queryKey: ["pricelistdocs", ns] });
      return { results, kept };
    },
  });
  const failed = apply.data?.results.filter((r) => !r.ok) ?? [];
  const speak = speaksGst(seller.data);
  const words = (p: { pricingmode: string; amount: number } | undefined, gst: number | undefined) =>
    p ? rulePays(p, gst, speak) : "—";
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Revert to {KIND_WORDS[doc.kind].toLowerCase()} #{doc.id}: {doc.title}</DialogTitle>
      <DialogContent>
        {compare.isLoading && <Typography variant="body2">Comparing with billing…</Typography>}
        {compare.isError && <Alert severity="error">{(compare.error as Error).message}</Alert>}
        {compare.data?.error && <Alert severity="error">{compare.data.error}</Alert>}
        {lines.length > 0 && (
          <Table size="small" aria-label="Compare with billing" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Price for</TableCell>
                <TableCell>Billing now</TableCell>
                <TableCell>This list</TableCell>
                <TableCell>What happens</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l) => {
                const gst = lineGst(l, comps.data ?? [], seller.data);
                const res = apply.data?.results.find((r) => r.line === l);
                return (
                  <TableRow key={JSON.stringify([l.labelselector, l.parentlabelselector])}
                    sx={l.action === "same" ? { opacity: 0.6 } : undefined}>
                    <TableCell>
                      {describeSelector(l.labelselector)}
                      {l.parentlabelselector && Object.keys(l.parentlabelselector).length > 0 ? ` · with ${describeSelector(l.parentlabelselector)}` : ""}
                    </TableCell>
                    <TableCell>{words(l.billing, gst)}</TableCell>
                    <TableCell>{words(l.document, gst)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={ACTION_WORDS[l.action]}
                        color={l.action === "change" || l.action === "revert" ? "warning" : "default"} />
                      {l.reason && (
                        <Typography variant="caption" display="block" color="text.secondary">{l.reason}</Typography>
                      )}
                      {res && (
                        <Typography variant="caption" display="block" color={res.ok ? "success.main" : "error"}>{res.words}</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {compare.data && !compare.data.error && (
          <Stack spacing={1.5}>
            <TextField size="small" type="date" label="Apply from" value={from} InputLabelProps={{ shrink: true }} sx={{ maxWidth: 220 }}
              helperText="India date. A changed price starts tomorrow at the earliest; an undone change may go back to the day it began."
              onChange={(e) => setFrom(e.target.value)} />
            {plan.problems.length > 0 && <Alert severity="warning">{plan.problems.join(". ")}</Alert>}
            {plan.steps.length === 0 && lines.length > 0 && (
              <Alert severity="info">Nothing to apply: every price is the same, or can only be changed with a new price list.</Alert>
            )}
            {apply.isError && <Alert severity="error">{apply.error.message}</Alert>}
            {apply.data?.kept && (
              <Alert severity="success">
                Every price applied. Kept as document #{apply.data.kept.id} (Applied to billing, from {from}).
              </Alert>
            )}
            {apply.data && failed.length > 0 && (
              <Alert severity="error">
                <AlertTitle>{failed.length} line(s) not applied</AlertTitle>
                The others are done. Fix what is said beside each line and choose Run again: done lines are not changed twice.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {apply.data && failed.length > 0 ? (
          <Button variant="contained" color="warning" disabled={apply.isPending}
            onClick={() => { apply.reset(); void compare.refetch(); }}>
            Run again
          </Button>
        ) : (
          <Button variant="contained" color="warning" disabled={apply.isPending || plan.steps.length === 0 || plan.problems.length > 0 || Boolean(apply.data)}
            onClick={() => apply.mutate()}>
            {apply.isPending ? "Applying…" : `Apply ${plan.steps.length} change(s)`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
